/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 照片预览缩略图组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-08-26 13:58:38
 */

import React, { useMemo } from 'react'

import useImage from 'use-image'
import ImageSelectionEditor from '../../components/ImageSelectionEditor'
import {
  calculateImageDisplaySize,
  optimizeAliyunImageUrl
} from '../../../utils/imageUtils'
import {
  DeleteOutlined,
  FullscreenOutlined,
  SwapOutlined
} from '@ant-design/icons'

// 缩略图操作图标查表（icon 来自 iconActions prop，universe = fullscreen/swap/delete）
const ACTION_ICONS: Record<string, React.ComponentType<any>> = {
  fullscreen: FullscreenOutlined,
  swap: SwapOutlined,
  delete: DeleteOutlined
}
const ActionIcon = ({ name }: { name: string }) => {
  const C = ACTION_ICONS[name]
  return C ? <C /> : null
}

export interface XCIconAction {
  type: 'zoom' | 'swap' | 'delete'
  icon: string
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
}

interface PhotoPreviewThumbnailProps {
  imageUrl: string
  width?: number
  height?: number
  onClick?: (type: string) => void
  style?: React.CSSProperties
  showFullscreenIcon?: boolean
  iconActions?: XCIconAction[]
  /** bbox 数据，格式为 [[x, y, width, height], ...] */
  bbox?: number[][]
}

/**
 * 照片预览缩略图组件
 * @param imageUrl 图片URL
 * @param width 宽度，默认200
 * @param height 高度，默认200
 * @param onClick 点击回调，接收type参数
 * @param style 额外样式
 * @param showFullscreenIcon 是否显示放大图标，默认true（向后兼容）
 * @param iconActions 图标操作配置数组
 * @param bbox 图片选中的区域，格式为 [[x1, y1, x2, y2], ...]
 */
const PhotoPreviewThumbnail: React.FC<PhotoPreviewThumbnailProps> = ({
  imageUrl,
  width = 200,
  height = 200,
  onClick,
  style = {},
  showFullscreenIcon = true,
  iconActions,
  bbox
}) => {
  // 优化图片URL，添加宽度参数以节省流量
  const optimizedImageUrl = optimizeAliyunImageUrl(imageUrl, 400)

  // 加载原始图片以获取正确的原始尺寸（用于bbox坐标转换）
  const [originalImage] = useImage(imageUrl || '')
  // 加载优化后的图片用于显示
  const [image] = useImage(optimizedImageUrl || '')

  // 计算图片显示尺寸
  const imageDisplaySize = useMemo(() => {
    if (!originalImage) {
      return { width, height }
    }
    return calculateImageDisplaySize(
      { width: originalImage.width, height: originalImage.height },
      width,
      height
    )
  }, [originalImage, width, height])

  // 将 bbox 转换为 ImageSelectionEditor 所需的选区格式
  const initialSelections = useMemo(() => {
    if (!bbox || !originalImage) {
      return []
    }

    return bbox.map(([x, y, width, height]) => {
      // 计算缩放比例
      const scaleX = imageDisplaySize.width / originalImage.width
      const scaleY = imageDisplaySize.height / originalImage.height

      return {
        x: Math.round(x * scaleX),
        y: Math.round(y * scaleY),
        width: Math.round(width * scaleX),
        height: Math.round(height * scaleY)
      }
    })
  }, [bbox, originalImage, imageDisplaySize])

  if (!imageUrl) {
    return null
  }

  // 处理图标点击事件
  const handleIconClick = (e: React.MouseEvent, type: string) => {
    e.stopPropagation() // 阻止事件冒泡
    if (onClick) {
      onClick(type)
    }
  }

  // 获取图标背景色
  const getIconBackgroundColor = (type: string) => {
    switch (type) {
      case 'zoom':
        return 'rgba(25, 25, 25, 0.5)' // 蓝色半透明
      case 'swap':
        return 'rgba(189, 243, 250, 0.7)' // 绿色半透明
      case 'delete':
        return 'rgba(255, 77, 79, 0.7)' // 红色半透明
      default:
        return 'rgba(0, 0, 0, 0.5)' // 黑色半透明
    }
  }

  // 获取位置样式
  const getPositionStyle = (
    position: string = 'bottom-right',
    type: string
  ) => {
    const backgroundColor = getIconBackgroundColor(type)

    const baseStyle = {
      position: 'absolute' as const,
      color: 'white',
      fontSize: 16,
      backgroundColor,
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      cursor: 'pointer',
      zIndex: 10
    }

    switch (position) {
      case 'bottom-left':
        return { ...baseStyle, bottom: 4, left: 4 }
      case 'top-right':
        return { ...baseStyle, top: 4, right: 4 }
      case 'top-left':
        return { ...baseStyle, top: 4, left: 4 }
      case 'bottom-right':
      default:
        return { ...baseStyle, bottom: 4, right: 4 }
    }
  }

  // 默认图标配置（向后兼容）
  const defaultIcons: XCIconAction[] = showFullscreenIcon
    ? [{ type: 'zoom', icon: 'fullscreen', position: 'bottom-right' }]
    : []

  // 使用传入的图标配置或默认配置
  const finalIconActions = iconActions || defaultIcons

  // 如果有 bbox 且图片已加载，使用 ImageSelectionEditor
  if (bbox && bbox.length > 0 && image) {
    return (
      <div
        className="photo-thumbnail"
        style={{
          position: 'relative',
          width,
          height,
          border: '1px solid #d9d9d9',
          borderRadius: '4px',
          overflow: 'hidden',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...style
        }}
        onClick={() => onClick && onClick('zoom')} // 默认点击为放大
      >
        <div
          style={{
            width: imageDisplaySize.width,
            height: imageDisplaySize.height,
            maxWidth: '100%',
            maxHeight: '100%'
          }}
        >
          <ImageSelectionEditor
            src={optimizedImageUrl}
            width={imageDisplaySize.width}
            height={imageDisplaySize.height}
            hasSelection={true}
            selectionType="multiple"
            initialSelections={initialSelections}
            disabled={true} // 禁用所有交互，仅用于展示
          />
        </div>
        {finalIconActions.map((action, index) => (
          <div
            key={`${action.type}-${index}`}
            style={getPositionStyle(action.position, action.type)}
            onClick={(e) => handleIconClick(e, action.type)}
          >
            <ActionIcon name={action.icon} />
          </div>
        ))}
      </div>
    )
  }

  // 原来的背景图片方式
  return (
    <div
      className="photo-thumbnail"
      style={{
        position: 'relative',
        width,
        height,
        backgroundImage: `url(${optimizedImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        cursor: 'pointer',
        border: '1px solid #d9d9d9',
        borderRadius: '4px',
        ...style
      }}
      onClick={() => onClick && onClick('zoom')} // 默认点击为放大
    >
      {finalIconActions.map((action, index) => (
        <div
          key={`${action.type}-${index}`}
          style={getPositionStyle(action.position, action.type)}
          onClick={(e) => handleIconClick(e, action.type)}
        >
          <ActionIcon name={action.icon} />
        </div>
      ))}
    </div>
  )
}

export default PhotoPreviewThumbnail
